import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { CalendarClock, ChevronLeft, ChevronRight, Phone, Plus, Users } from 'lucide-react';
import {
  RESERVATION_CHANNEL_LABELS,
  RESERVATION_STATUS_LABELS,
  type Reservation,
  type ReservationStatus,
} from '@sebastian/shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatClock, pluralise } from '../lib/format';
import { useTables } from '../hooks/queries';
import { useNow } from '../hooks/useNow';
import { useToast } from '../hooks/useToast';
import { BookingDialog, SeatDialog, localDateTime, todayLocal } from '../components/BookingDialogs';
import { Badge, Button, Card, EmptyState, ErrorState, Input, SectionHeading, Spinner, Stat } from '../components/ui';

const STATUS_TONE: Record<ReservationStatus, 'neutral' | 'accent' | 'positive' | 'caution' | 'danger' | 'info'> = {
  BOOKED: 'accent',
  SEATED: 'positive',
  NO_SHOW: 'danger',
  CANCELLED: 'neutral',
};

type Filter = 'ALL' | 'TO_ARRIVE' | 'SEATED' | 'CLOSED';
const FILTERS: { key: Filter; label: string; match: (r: Reservation) => boolean }[] = [
  { key: 'ALL', label: 'All', match: () => true },
  { key: 'TO_ARRIVE', label: 'To arrive', match: (r) => r.status === 'BOOKED' },
  { key: 'SEATED', label: 'Seated', match: (r) => r.status === 'SEATED' },
  { key: 'CLOSED', label: 'No-show & cancelled', match: (r) => r.status === 'NO_SHOW' || r.status === 'CANCELLED' },
];

const PARTY_SIZES = [2, 4, 6, 8];

function shiftDate(date: string, days: number): string {
  const value = new Date(`${date}T12:00:00`);
  value.setDate(value.getDate() + days);
  return localDateTime(value.toISOString()).date;
}

function visitLabel(booking: Reservation): string | null {
  if (!booking.phone) return null;
  if (booking.previousVisits === 0) return 'New guest';
  return `Returning · ${pluralise(booking.previousVisits, 'visit')} before`;
}

/**
 * The manager's booking book for one day: who is coming, when, how many, on
 * which table — and, beside it, how full each slot is on Bistrochat.
 */
export function BookingsPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { allows } = useAuth();
  const canManage = allows('reservations:manage');
  const now = useNow(30_000);

  const [date, setDate] = useState(todayLocal());
  const [filter, setFilter] = useState<Filter>('ALL');
  const [partySize, setPartySize] = useState(2);
  const [editing, setEditing] = useState<Reservation | null | 'new'>(null);
  const [seating, setSeating] = useState<Reservation | null>(null);

  const { data: tables } = useTables();
  const activeTables = useMemo(() => (tables ?? []).filter((table) => table.isActive), [tables]);

  const { data: bookings, isLoading, error, refetch } = useQuery({
    queryKey: ['reservations', date],
    queryFn: () => api.reservations.list(date),
    refetchInterval: 30_000,
  });

  const availability = useQuery({
    queryKey: ['reservations', 'availability', date, partySize],
    queryFn: () => api.reservations.availability(date, partySize),
    refetchInterval: 60_000,
    retry: false,
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Exclude<ReservationStatus, 'SEATED'> }) =>
      api.reservations.setStatus(id, status),
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: ['reservations'] });
      toast.success(`${updated.guestName}: ${RESERVATION_STATUS_LABELS[updated.status].toLowerCase()}.`);
    },
    onError: (caught: unknown) =>
      toast.error(caught instanceof ApiError ? caught.message : 'Could not update the booking.'),
  });

  const list = bookings ?? [];
  const live = list.filter((b) => b.status === 'BOOKED' || b.status === 'SEATED');
  const stats = {
    bookings: live.length,
    covers: live.reduce((sum, b) => sum + b.partySize, 0),
    toArrive: list.filter((b) => b.status === 'BOOKED').length,
    seated: list.filter((b) => b.status === 'SEATED').length,
    noShows: list.filter((b) => b.status === 'NO_SHOW').length,
  };
  const shown = list.filter(FILTERS.find((entry) => entry.key === filter)!.match);
  const isToday = date === todayLocal();

  /** Our own bookings that start in each half hour, next to Bistrochat's count. */
  const bookedInSlot = (startAt: string) => {
    const start = new Date(startAt).getTime();
    return live.filter((b) => {
      const t = new Date(b.reservedAt).getTime();
      return t >= start && t < start + 30 * 60_000;
    }).length;
  };

  const availabilityError = availability.error instanceof ApiError ? availability.error : null;
  const notConnected = availabilityError?.code === 'BOOKINGS_NOT_CONFIGURED';

  return (
    <div className="mx-auto max-w-[1300px] px-5 py-6">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-wide text-ink">Bookings</h1>
          <p className="mt-1 text-sm text-ink-faint">
            Who is coming, when, and on which table. Seat a party to open their table.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="ghost" icon={<ChevronLeft size={16} />} onClick={() => setDate(shiftDate(date, -1))}>
            Previous
          </Button>
          <Input type="date" value={date} onChange={(event) => event.target.value && setDate(event.target.value)} className="w-44" />
          <Button size="sm" variant="ghost" onClick={() => setDate(shiftDate(date, 1))}>
            Next <ChevronRight size={16} className="ml-1 inline" />
          </Button>
          {!isToday ? (
            <Button size="sm" variant="secondary" onClick={() => setDate(todayLocal())}>
              Today
            </Button>
          ) : null}
          {canManage ? (
            <Button variant="primary" icon={<Plus size={16} />} onClick={() => setEditing('new')}>
              New booking
            </Button>
          ) : null}
        </div>
      </header>

      <div className="mb-5 flex flex-wrap gap-8 rounded-card border border-line bg-surface px-5 py-4">
        <Stat label="Bookings" value={stats.bookings} />
        <Stat label="Covers" value={stats.covers} tone="accent" />
        <Stat label="To arrive" value={stats.toArrive} />
        <Stat label="Seated" value={stats.seated} />
        <Stat label="No-shows" value={stats.noShows} />
      </div>

      {/* ------------------------- Bistrochat slots ------------------------- */}
      <Card className="mb-5">
        <SectionHeading
          title="Bistrochat — online availability"
          subtitle="Tables guests can still book online, per half hour, for the party size chosen. A slot also shows full when Bistrochat has closed it to online booking. Refreshes every minute."
          action={
            <div className="flex gap-1.5">
              {PARTY_SIZES.map((size) => (
                <button
                  key={size}
                  type="button"
                  onClick={() => setPartySize(size)}
                  className={clsx(
                    'h-9 min-w-9 rounded-lg border px-2.5 text-sm font-medium transition',
                    partySize === size
                      ? 'border-accent bg-accent/15 text-accent-strong'
                      : 'border-line text-ink-soft hover:border-line-strong',
                  )}
                >
                  <Users size={13} className="mr-1 inline align-[-2px]" />
                  {size}
                </button>
              ))}
            </div>
          }
        />
        {availability.isLoading ? (
          <Spinner label="Asking Bistrochat…" />
        ) : notConnected ? (
          <p className="text-sm text-ink-faint">
            Bistrochat is not connected.{' '}
            {allows('admin:settings') ? 'Add the token in Settings → Bookings (Bistrochat).' : 'Ask an admin to connect it in Settings.'}
          </p>
        ) : availabilityError ? (
          <p className="text-sm text-danger">{availabilityError.message}</p>
        ) : availability.data && availability.data.slots.length === 0 ? (
          <p className="text-sm text-ink-faint">No bookable slots on Bistrochat for this day.</p>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(8.5rem,1fr))] gap-2">
            {(availability.data?.slots ?? []).map((slot) => {
              const share = slot.totalSpot > 0 ? slot.booked / slot.totalSpot : 0;
              const full = slot.totalSpot > 0 && slot.openSpot === 0;
              const ours = bookedInSlot(slot.startAt);
              return (
                <div
                  key={slot.startAt}
                  className={clsx(
                    'rounded-lg border px-3 py-2.5',
                    full ? 'border-danger/50 bg-danger/8' : 'border-line bg-canvas',
                  )}
                >
                  <div className="flex items-baseline justify-between">
                    <span className="tnum font-display text-lg text-ink">{formatClock(slot.startAt)}</span>
                    <span className={clsx('text-[11px] font-semibold uppercase', full ? 'text-danger' : 'text-ink-faint')}>
                      {full ? 'Full online' : `${slot.openSpot} free`}
                    </span>
                  </div>
                  <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-line">
                    <div
                      className={clsx('h-full rounded-full', full ? 'bg-danger' : share > 0.7 ? 'bg-caution' : 'bg-accent')}
                      style={{ width: `${Math.round(share * 100)}%` }}
                    />
                  </div>
                  <div className="tnum mt-1 text-[11px] text-ink-faint">
                    {slot.booked}/{slot.totalSpot} not bookable
                    {ours > 0 ? ` · ${ours} in book` : ''}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* --------------------------- booking list --------------------------- */}
      <div className="mb-4 flex flex-wrap gap-2">
        {FILTERS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            onClick={() => setFilter(entry.key)}
            className={clsx(
              'h-10 rounded-full border px-4 text-sm font-medium transition',
              filter === entry.key
                ? 'border-accent bg-accent/12 text-accent-strong'
                : 'border-line text-ink-soft hover:border-line-strong hover:text-ink',
            )}
          >
            {entry.label}
            <span className="tnum ml-2 text-xs text-ink-faint">{list.filter(entry.match).length}</span>
          </button>
        ))}
      </div>

      {isLoading ? <Spinner label="Loading bookings…" /> : null}
      {error ? <ErrorState error={error} onRetry={() => void refetch()} /> : null}
      {bookings && shown.length === 0 ? (
        <EmptyState
          icon={<CalendarClock size={30} />}
          title={list.length === 0 ? 'No bookings for this day' : 'Nothing in this view'}
          message={canManage && list.length === 0 ? 'Add the first one with “New booking”.' : undefined}
        />
      ) : null}

      <div className="space-y-2">
        {shown.map((booking) => {
          const lateMinutes =
            booking.status === 'BOOKED' ? Math.floor((now - new Date(booking.reservedAt).getTime()) / 60_000) : 0;
          const visits = visitLabel(booking);
          return (
            <div
              key={booking.id}
              className={clsx(
                'flex flex-wrap items-center gap-4 rounded-card border bg-surface px-4 py-3',
                booking.status === 'CANCELLED' || booking.status === 'NO_SHOW' ? 'border-line opacity-55' : 'border-line',
                lateMinutes >= 15 && 'border-caution/60',
              )}
            >
              <div className="w-16 shrink-0">
                <div className="tnum font-display text-2xl leading-none text-ink">{formatClock(booking.reservedAt)}</div>
                {lateMinutes >= 15 ? <div className="mt-1 text-[11px] font-semibold text-caution">Late {lateMinutes} min</div> : null}
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-ink">{booking.guestName}</span>
                  <Badge tone={STATUS_TONE[booking.status]}>{RESERVATION_STATUS_LABELS[booking.status]}</Badge>
                  <Badge tone="neutral">{RESERVATION_CHANNEL_LABELS[booking.channel]}</Badge>
                  {booking.guestSource ? <Badge tone="neutral">{booking.guestSource}</Badge> : null}
                  {visits ? <Badge tone={booking.previousVisits > 0 ? 'accent' : 'positive'}>{visits}</Badge> : null}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-ink-faint">
                  <span className="tnum">
                    <Users size={12} className="mr-1 inline align-[-1px]" />
                    {pluralise(booking.partySize, 'guest')}
                  </span>
                  <span>{booking.tableNumber ? `Table ${booking.tableNumber}` : 'No table yet'}</span>
                  {booking.phone ? (
                    <a href={`tel:${booking.phone}`} className="hover:text-ink">
                      <Phone size={12} className="mr-1 inline align-[-1px]" />
                      {booking.phone}
                    </a>
                  ) : null}
                  {booking.seatedAt ? <span>Seated {formatClock(booking.seatedAt)}</span> : null}
                </div>
                {booking.notes ? <div className="mt-1 text-xs italic text-caution">{booking.notes}</div> : null}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {booking.status === 'BOOKED' ? (
                  <>
                    <Button size="sm" variant="primary" onClick={() => setSeating(booking)}>
                      Seat
                    </Button>
                    {canManage ? (
                      <>
                        <Button size="sm" variant="secondary" onClick={() => setEditing(booking)}>
                          Edit
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setStatus.mutate({ id: booking.id, status: 'NO_SHOW' })}>
                          No-show
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setStatus.mutate({ id: booking.id, status: 'CANCELLED' })}>
                          Cancel
                        </Button>
                      </>
                    ) : null}
                  </>
                ) : null}
                {booking.status === 'SEATED' && booking.tableId ? (
                  <Button size="sm" variant="secondary" onClick={() => navigate(`/order/${booking.tableId}`)}>
                    Open table
                  </Button>
                ) : null}
                {(booking.status === 'NO_SHOW' || booking.status === 'CANCELLED') && canManage ? (
                  <Button size="sm" variant="ghost" onClick={() => setStatus.mutate({ id: booking.id, status: 'BOOKED' })}>
                    Undo
                  </Button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      <BookingDialog
        open={editing !== null}
        booking={editing === 'new' ? null : editing}
        defaultDate={date}
        tables={activeTables}
        onClose={() => setEditing(null)}
      />
      <SeatDialog
        booking={seating}
        tables={activeTables}
        onClose={() => setSeating(null)}
        onSeated={(seated) => {
          setSeating(null);
          if (seated.tableId) navigate(`/order/${seated.tableId}`);
        }}
      />
    </div>
  );
}
